import type { ReactNode } from 'react';
import styles from './SegmentedControl.module.css';

interface Option<T extends string> {
  id: T;
  label: string;
  /** Optional count chip rendered after the label (e.g. a "+12" today badge).
      Callers that omit it get the plain label, unchanged. */
  badge?: ReactNode;
}

interface ISegmentedControlProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: ReadonlyArray<Option<T>>;
  ariaLabel: string;
  asTabs?: boolean;
  /** `pill` (default): Trusstor's grey switcher rail. `outlined`: joined
   *  bordered boxes, the selected one ringed — Trusstor's calendar range switch.
   *  `joined`: one bordered group with hairline separators, the selected
   *  segment info-tinted — the filter-pill vocabulary (the Daily List's kind
   *  scope, the Work Plan's grouping). `wrap`: separate grey pills that wrap
   *  onto more lines, the selected one navy — for a narrow column where the
   *  options would not fit on one line (the Monitor rail's filter pills). */
  variant?: 'pill' | 'outlined' | 'joined' | 'wrap';
  size?: 'md' | 'sm';
}

const VARIANT_CLASS = { pill: '', outlined: styles.outlined, joined: styles.joined, wrap: styles.wrap };
const SIZE_CLASS = { md: '', sm: styles.sm };

export function SegmentedControl<T extends string>({ value, onChange, options, ariaLabel, asTabs = false, variant = 'pill', size = 'md' }: ISegmentedControlProps<T>) {
  return (
    <div role={asTabs ? 'tablist' : 'group'} aria-label={ariaLabel} className={`${styles.root} ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]}`}>
      {options.map((opt) => {
        const selected = opt.id === value;
        // The badge is a sibling element of the label, and the accessible-name
        // builder trims each node before joining — so a numeric badge made the
        // name read "Active1". Naming the button explicitly fixes that without
        // putting a whitespace text node into the flex row (which would become
        // an anonymous flex item and widen the pill). Only text-ish badges are
        // folded in; an element badge falls back to the derived name rather
        // than being stringified into "[object Object]".
        const badgeText =
          typeof opt.badge === 'string' || typeof opt.badge === 'number'
            ? String(opt.badge)
            : null;
        return (
          <button
            key={opt.id}
            type="button"
            {...(asTabs ? { role: 'tab', 'aria-selected': selected } : { 'aria-pressed': selected })}
            aria-label={badgeText ? `${opt.label} ${badgeText}` : undefined}
            data-label={opt.label}
            className={selected ? `${styles.segment} ${styles.segmentSelected}` : styles.segment}
            onClick={() => onChange(opt.id)}
          >
            {/* The label reserves its own bold (selected) width so toggling the
                selection never shifts the pill; the badge sits beside it. */}
            <span className={styles.label} data-label={opt.label}>
              <span>{opt.label}</span>
            </span>
            {opt.badge != null && <span className={styles.badge}>{opt.badge}</span>}
          </button>
        );
      })}
    </div>
  );
}
